import React, { useState } from "react";
import { MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useGetOrganisationInvoicesQuery, useVoidInvoiceMutation } from "@/store/admin_api";
import { Invoice } from "@/model/interface/Invoice";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_PILLS,
  formatInvoiceDate,
  formatInvoiceMoney,
  formatInvoicePeriod,
  invoiceDocumentName,
} from "@/util/invoice";
import { apiErrorMessage } from "@/util/apiError";

const INVOICES_PER_PAGE = 25;

interface OrganisationInvoicesTabProps {
  organisationId: string;
}

export default function OrganisationInvoicesTab({ organisationId }: OrganisationInvoicesTabProps) {
  const [page, setPage] = useState(0);
  const [voidTarget, setVoidTarget] = useState<Invoice | null>(null);
  const { data: invoices, isFetching } = useGetOrganisationInvoicesQuery({
    organisationId,
    page,
    size: INVOICES_PER_PAGE,
  });
  const [voidInvoice, { isLoading: isVoiding }] = useVoidInvoiceMutation();

  const totalPages = invoices?.totalPages ?? 0;

  const invoiceLabel = (invoice: Invoice) => (invoice.number ? invoiceDocumentName(invoice) : "Unnumbered draft");

  const handleVoid = async (invoice: Invoice) => {
    try {
      await voidInvoice(invoice.id).unwrap();
      toast.success(`${invoiceLabel(invoice)} voided`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not void the invoice"));
    }
  };

  const handleVoidDialogChange = (open: boolean) => {
    if (!open) {
      setVoidTarget(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
        <CardDescription>
          Every invoice run for this organisation, newest first. Drafts and failed runs show here only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isFetching && <Skeleton className="h-64 w-full" />}

        {!isFetching && invoices?.items.length === 0 && (
          <p className="py-6 text-sm text-muted-foreground">This organisation has no invoices yet.</p>
        )}

        {!isFetching && invoices && invoices.items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.items.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium tabular-nums">
                      {invoiceLabel(invoice)}
                      {invoice.stripeInvoiceId && (
                        <span className="block font-mono text-xs font-normal text-muted-foreground">
                          {invoice.stripeInvoiceId}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatInvoicePeriod(invoice.usagePeriodStart, invoice.usagePeriodEnd) ||
                        formatInvoicePeriod(invoice.planPeriodStart, invoice.planPeriodEnd)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatInvoiceDate(invoice.createdAt)}</TableCell>
                    <TableCell className="max-w-xs">
                      <span className={INVOICE_STATUS_PILLS[invoice.status]}>{INVOICE_STATUS_LABELS[invoice.status]}</span>
                      {invoice.refunds && invoice.refunds.length > 0 && (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {formatInvoiceMoney(invoice.refunds.reduce((total, refund) => total + refund.amount, 0))} refunded
                        </span>
                      )}
                      {invoice.failureReason && (
                        <span className="mt-1 block text-xs text-destructive">{invoice.failureReason}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatInvoiceMoney(invoice.taxTotal)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatInvoiceMoney(invoice.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      {invoice.status !== "VOID" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" title="Options">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="text-destructive" onClick={() => setVoidTarget(invoice)}>
                              Void invoice
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground tabular-nums">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((previous) => Math.max(0, previous - 1))}
                disabled={page === 0 || isFetching}
              >
                <FaChevronLeft className="mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((previous) => previous + 1)}
                disabled={page + 1 >= totalPages || isFetching}
              >
                Next
                <FaChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={voidTarget !== null} onOpenChange={handleVoidDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void {voidTarget ? invoiceLabel(voidTarget) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              The customer still sees the invoice, marked void. Any recorded sales tax transaction is reversed in
              Stripe. Nothing is refunded to the card.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isVoiding}
              onClick={() => voidTarget && handleVoid(voidTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Void invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
